import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import LaunchIcon from '@mui/icons-material/Launch';
import {
  Button,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  Grid,
  Link,
  Avatar
} from '@mui/material';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

import { getIcon, getIssueImage, getLogo } from '../../api/aws/s3';
import { db } from '../../api/firebase/config';
import {
  companiesRef,
  companySnapshot
} from '../../api/firebase/firestore/company';
import { issueSnapshot, issuesRef } from '../../api/firebase/firestore/issue';
import { mediaSnapshot, mediasRef } from '../../api/firebase/firestore/media';
import { userSnapshot, usersRef } from '../../api/firebase/firestore/user';
import noImageIcon from '../../components/assets/noImageIcon.svg';
import {
  black,
  blue,
  blueButton,
  primaryButtonBackground,
  tertiaryButtonBackground
} from '../../constants/Colors';
import { mypageHeader, mypageHeaderTitle } from '../../constants/Location';
import {
  contact as contactKind,
  negotiationStatus,
  screenType
} from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';

import CustomDatePicker from './CustomDatePicker';
import CustomGuideButton from './CustomGuideButton';

import type * as authType from '../../api/firebase/auth/authType';
import type { Issue } from '../../api/firebase/firestore/issue/issueType';
import type { Dayjs } from 'dayjs';
import type { DocumentData, Timestamp } from 'firebase/firestore';

const styles = {
  card: {
    '@media screen and (max-width:480px)': {
      margin: '16px',
      padding: '24px 16px'
    },
    border: '1px solid #DBDBE0',
    borderRadius: '4px',
    boxShadow: 0,
    margin: '24px',
    padding: '40px',
    position: 'relative'
  },
  contactButton: {
    '@media screen and (max-width:960px)': {
      height: '30px',
      padding: '4px 10px'
    },
    padding: '6px 16px'
  },
  contactButtonText: {
    color: black,
    fontSize: '14px'
  },
  logo: {
    height: '32px',
    width: '32px'
  },
  mypageHeader,
  mypageHeaderTitle,
  table: {
    borderRadius: '4px',
    boxShadow: 0
  },
  tableAmountCell: {
    minWidth: '185px'
  },
  tableCell: {
    borderBottom: 'none',
    borderRight: '1px solid #DBDBE0',
    boxSizing: 'border-box',
    color: black,
    minWidth: '172px',
    textAlign: 'center'
  },
  tableHeaderCell: {
    fontSize: '14px',
    fontWeight: 700
  },
  tableMediaNameCell: {
    fontSize: '18px',
    fontWeight: 700
  },
  tableName: {
    fontSize: '14px',
    textAlign: 'left'
  },
  tableNameCell: {
    alignItems: 'center',
    columnGap: '8px',
    display: 'flex',
    flexDirection: 'row'
  },
  tableNumberCell: {
    fontSize: '24px',
    fontWeight: 700
  },
  tableRow: {
    border: '1px solid #DBDBE0'
  },
  tableUnit: {
    fontSize: '14px',
    fontWeight: 700
  },
  tableWrapper: {
    boxShadow: 'none'
  },
  tertiaryButtonBackground,
  todo: {
    backgroundColor: '#F2F2F2',
    borderBottom: '1px solid #DBDBE0',
    color: black,
    fontSize: '14px',
    fontWeight: 700,
    padding: '10px 16px'
  },
  todoBody: {
    padding: '24px'
  },
  todoCard: {
    border: '1px solid #DBDBE0',
    borderRadius: '4px',
    boxShadow: 0,
    mt: '24px',
    position: 'relative'
  },
  todoLink: {
    color: blue
  },
  todoText: {
    color: black,
    fontSize: '18px',
    fontWeight: 700
  },
  usefulButton: {
    '@media screen and (max-width:960px)': {
      width: '60%'
    },
    margin: 'auto',
    marginBottom: '24px',
    width: '30%'
  }
};

type StatusCount = Record<string, number>;
type CountByIssueId = Record<string, StatusCount>;
type CountByMediaId = Record<string, StatusCount>;
type IssueSum = Record<string, number>;
type CountByPublisherMainUidAndMediaId = Record<string, CountByMediaId>;
type SumByPublisherMainUidAndMediaId = Record<string, Record<string, number>>;

const MypageHome: React.FC<authType.RegisterScreenProps> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const [company, setCompany] = useState<{ name: string; email: string }>({
    email: '',
    name: ''
  });
  const [companyId, setCompanyId] = useState<string>('');

  useEffect(() => {
    void (async () => {
      if (currentUser) {
        const userRef = doc(usersRef, currentUser.uid);
        const userSnap = await getDoc(userRef);
        setCompanyId(userSnap.data()?.companyId as string);
        if (userSnap.exists()) {
          const ownCampanyRef = doc(
            companiesRef,
            userSnap.data()?.companyId as string
          );
          const companySnap = await getDoc(ownCampanyRef);
          if (companySnap.exists()) {
            setCompany({
              email: userSnap.data()?.email as string,
              name: companySnap.data()?.name as string
            });
          }
        }
      }
    })();
  }, []);

  // お問い合わせ関連
  const contact = t('mypage.contact');
  const contactUrl = '/common/contact';
  const screenTypeHelper = (): string => {
    if (props.screenType === screenType.client) {
      return 'client';
    } else if (props.screenType === screenType.agent) {
      return 'agent';
    } else if (props.screenType === screenType.publisher) {
      return 'publisher';
    }
    return '';
  };

  interface ClientTableItem {
    icon: string;
    name: string;
    requestProposal: number;
    negotiation: number;
    waitPublished: number;
    published: number;
    amount: number;
  }

  interface PublisherTableItem {
    icon: string;
    name: string;
    mediaName: string;
    like: number;
    proposalPreparation: number;
    negotiation: number;
    orderApproval: number;
    amount: number;
  }

  interface AgentTableItem {
    icon: string;
    name: string;
    requestProposal: number;
    negotiation: number;
    waitPublished: number;
    published: number;
    amount: number;
  }

  // 出し分けTODO
  const [suggestion, setSuggestion] = useState<string>('');
  const [suggestionText, setSuggestionText] = useState<string>('');
  const [suggestionDetail, setSuggestionDetail] = useState<string>('');
  const [detailUrl, setDetailUrl] = useState<string>('');
  const [suggestionButton, setSuggestionButton] = useState<string>('');
  const [buttonLink, setButtonLink] = useState<string>('');

  const [showStaffData, setShowStaffData] = useState(false);
  const [showData, setShowData] = useState(true);

  // テーブル項目
  const [tableItem, setTableItem] = useState<string[]>([]);

  // 広告主の会社毎データ
  const [clientTableValue, setClientTableValue] = useState<ClientTableItem[]>(
    []
  );
  // 広告主のスタッフ毎データ
  const [clientStaffTableValue, setClientStaffTableValue] = useState<
    ClientTableItem[]
  >([]);

  // 媒体社の会社毎データ
  const [publisherTableValue, setPublisherTableValue] = useState<
    PublisherTableItem[]
  >([]);
  // 媒体社のスタッフ毎データ
  const [publisherStaffTableValue, setPublisherStaffTableValue] = useState<
    PublisherTableItem[]
  >([]);

  // 代理店のデータ
  const [agentTableValue, setAgentTableValue] = useState<AgentTableItem[]>([]);

  // 代理店のスタッフ毎データ
  const [agentStaffTableValue, setAgentStaffTableValue] = useState<
    AgentTableItem[]
  >([]);

  // スタッフの表示
  const handleSwitchChange = (): void => {
    setShowStaffData(!showStaffData);
    setShowData(!showData);
  };

  const transformDataToTableItem = async (
    countsByIssueId: CountByIssueId,
    sumsByIssueId: IssueSum
  ): Promise<ClientTableItem[]> => {
    const promises = Object.entries(countsByIssueId).map(
      async ([issueId, statusCounts]) => {
        // statusに応じた数を取得 (存在しない場合は0を返す)
        const requestProposal =
          statusCounts[negotiationStatus.proposalPreparation] || 0;
        const negotiation = statusCounts[negotiationStatus.negotiation] || 0;
        const waitPublished = statusCounts[negotiationStatus.preparation] || 0;
        const published = statusCounts[negotiationStatus.posted] || 0;

        const issue = await issueSnapshot(issueId);
        const issueName = (issue.data()?.name as string) || '';
        let issueIcon = noImageIcon;
        if (issue.data()?.imageId) {
          issueIcon = await getIssueImage((issue.data() as Issue)?.imageId[0]);
        }

        return {
          amount: sumsByIssueId[issueId] || 0,
          icon: issueIcon,
          name: issueName,
          negotiation,
          published,
          requestProposal,
          waitPublished
        };
      }
    );
    return await Promise.all(promises);
  };

  const transformDataToTableItemByRepresentative = async (
    countsByClientMainUid: CountByIssueId,
    sumsByClientMainUid: IssueSum
  ): Promise<ClientTableItem[]> => {
    const promises = Object.entries(countsByClientMainUid).map(
      async ([clientMainUid, statusCounts]) => {
        const requestProposal =
          statusCounts[negotiationStatus.proposalPreparation] || 0;
        const negotiation = statusCounts[negotiationStatus.negotiation] || 0;
        const waitPublished = statusCounts[negotiationStatus.preparation] || 0;
        const published = statusCounts[negotiationStatus.posted] || 0;

        const client = await userSnapshot(clientMainUid);
        const clientName = (client.data()?.name as string) || '';
        const userIcon = await getIcon(client);

        return {
          amount: sumsByClientMainUid[clientMainUid] || 0,
          icon: userIcon,
          name: clientName,
          negotiation,
          published,
          requestProposal,
          waitPublished
        };
      }
    );
    return Promise.all(promises);
  };

  const transformDataToTableItemByRepresentativeAgent = async (
    countsByAgentMainUid: CountByIssueId,
    sumsByAgentMainUid: IssueSum
  ): Promise<ClientTableItem[]> => {
    const promises = Object.entries(countsByAgentMainUid).map(
      async ([agentMainUid, statusCounts]) => {
        const requestProposal =
          statusCounts[negotiationStatus.proposalPreparation] || 0;
        const negotiation = statusCounts[negotiationStatus.negotiation] || 0;
        const waitPublished = statusCounts[negotiationStatus.preparation] || 0;
        const published = statusCounts[negotiationStatus.posted] || 0;

        const agent = await userSnapshot(agentMainUid);
        const agentName = (agent.data()?.name as string) || '';
        const userIcon = await getIcon(agent);

        return {
          amount: sumsByAgentMainUid[agentMainUid] || 0,
          icon: userIcon,
          name: agentName,
          negotiation,
          published,
          requestProposal,
          waitPublished
        };
      }
    );
    return Promise.all(promises);
  };

  const transformDataToTableItemPublisher = async (
    countsByMediaId: CountByIssueId,
    sumsByMediaId: IssueSum
  ): Promise<PublisherTableItem[]> => {
    const promises = Object.entries(countsByMediaId).map(
      async ([mediaId, statusCounts]) => {
        // statusに応じた数を取得 (存在しない場合は0を返す)
        const like = statusCounts[negotiationStatus.like] || 0;
        const proposalPreparation =
          statusCounts[negotiationStatus.proposalPreparation] || 0;
        const negotiation = statusCounts[negotiationStatus.negotiation] || 0;
        const orderApproval =
          statusCounts[negotiationStatus.orderApproval] || 0;

        const media = await mediaSnapshot(mediaId);
        const mediaName = (media.data()?.name as string) || '';
        const user = await userSnapshot(currentUser?.uid as string);
        const publisherCompany = await companySnapshot(
          user.data()?.companyId as string
        );
        let companyIcon = noImageIcon;
        if (publisherCompany.data()?.logoId) {
          companyIcon = await getLogo(publisherCompany);
        }
        return {
          amount: sumsByMediaId[mediaId] || 0,
          icon: companyIcon,
          like,
          mediaName,
          name: publisherCompany.data()?.name as string,
          negotiation,
          orderApproval,
          proposalPreparation
        };
      }
    );
    return await Promise.all(promises);
  };

  const transformDataToTableItemByRepresentativePublisher = async (
    countsByPublisherMainUidAndMediaId: CountByPublisherMainUidAndMediaId,
    sumsByPublisherMainUidAndMediaId: SumByPublisherMainUidAndMediaId
  ): Promise<PublisherTableItem[]> => {
    const promises = Object.entries(countsByPublisherMainUidAndMediaId).flatMap(
      ([publisherMainUid, countsByMediaId]) =>
        Object.entries(countsByMediaId).map(async ([mediaId, statusCounts]) => {
          const like = statusCounts[negotiationStatus.like] || 0;
          const proposalPreparation =
            statusCounts[negotiationStatus.proposalPreparation] || 0;
          const negotiation = statusCounts[negotiationStatus.negotiation] || 0;
          const orderApproval =
            statusCounts[negotiationStatus.orderApproval] || 0;

          const media = await mediaSnapshot(mediaId);
          const mediaName = (media.data()?.name as string) || '';
          const user = await userSnapshot(publisherMainUid);
          const userIcon = await getIcon(user);

          const getAmount = (
            sums: SumByPublisherMainUidAndMediaId,
            mainUid: string,
            mId: string
          ): number => sums[mainUid]?.[mId] || 0;

          return {
            amount: getAmount(
              sumsByPublisherMainUidAndMediaId,
              publisherMainUid,
              mediaId
            ),
            icon: userIcon,
            like,
            mediaName,
            name: user.data()?.name as string,
            negotiation,
            orderApproval,
            proposalPreparation
          };
        })
    );
    return Promise.all(promises);
  };

  const [startDate, setStartDate] = useState<{
    start: Dayjs | null;
    end: Dayjs | null;
  }>({
    end: null,
    start: null
  });

  useEffect(() => {
    void (async () => {
      if (companyId && props.screenType === screenType.client) {
        // 与件の件数で表示だしわけ
        const q = query(issuesRef, where('companyId', '==', companyId));
        const { docs } = await getDocs(q);
        if (docs.length > 0) {
          setSuggestion(t('home.mediaInquiry'));
          setSuggestionText(t('home.mediaInquiryDesc'));
          setSuggestionDetail(t('home.searchMediaDetail'));
          setDetailUrl('');
          setSuggestionButton(t('home.searchMedia'));
          setButtonLink('/client/mypage/media');
        } else {
          setSuggestion(t('home.issueSuggest'));
          setSuggestionText(t('home.issueSuggestDesc'));
          setSuggestionDetail(t('home.issueDetail'));
          setDetailUrl('');
          setSuggestionButton(t('home.addIssue'));
          setButtonLink('/client/mypage/issue/edit?type=add');
        }
        setTableItem([
          t('home.tableItem.requestProposal'),
          t('home.tableItem.negotiation'),
          t('home.tableItem.preparingPub'),
          t('home.tableItem.alreadyPub'),
          t('home.tableItem.amount')
        ]);
        const user = await userSnapshot(currentUser?.uid ?? '');
        const negotiationsRef = collection(db, 'negotiations');
        const negotiations = query(
          negotiationsRef,
          where('clientId', '==', user.data()?.companyId)
        );

        const negotiationsSnapshot = await getDocs(negotiations);

        let negotiationsData = negotiationsSnapshot.docs.map((document) =>
          document.data()
        );
        await Promise.all(
          negotiationsData.map(async (negotiation, index) => {
            const ordersSnapshot = await getDocs(
              collection(
                doc(negotiationsRef, negotiation.negotiationId as string),
                'orders'
              )
            );
            const ordersData = ordersSnapshot.docs.map((document) =>
              document.data()
            );

            // Assuming there's always one order in the subcollection
            negotiationsData[index].order = ordersData[0];
          })
        );

        // Filter by date
        negotiationsData = negotiationsData.filter((negotiation) => {
          const approvedDate = (
            (negotiation.order as DocumentData)?.approvedDate as Timestamp
          )?.toDate();
          // both start and end date provided
          if (
            startDate.start &&
            startDate.end &&
            approvedDate >= startDate.start.toDate() &&
            approvedDate <= startDate.end.toDate()
          ) {
            return true;
          }
          // only start date provided
          if (
            startDate.start &&
            !startDate.end &&
            approvedDate >= startDate.start.toDate()
          ) {
            return true;
          }
          // only end date provided
          if (
            !startDate.start &&
            startDate.end &&
            approvedDate <= startDate.end.toDate()
          ) {
            return true;
          }
          // neither start nor end date provided
          if (!startDate.start && !startDate.end) {
            return true;
          }

          return false;
        });

        const countsByIssueId: CountByIssueId = {};
        const sumsByIssueId: IssueSum = {};

        // Process each negotiations doc and corresponding orders docs
        for (let i = 0; i < negotiationsData.length; i++) {
          const data = negotiationsData[i];
          const issueId = data.issueId as string;
          const status = data.status as number;

          // count status by issueId
          if (issueId in countsByIssueId) {
            if (status in countsByIssueId[issueId]) {
              countsByIssueId[issueId][status] += 1;
            } else {
              countsByIssueId[issueId][status] = 1;
            }
          } else {
            countsByIssueId[issueId] = { [status]: 1 };
          }

          // sum orders only for statuses 7 to 10
          if (
            status >= negotiationStatus.orderApproval &&
            status <= negotiationStatus.posted
          ) {
            const sum = (data.order as DocumentData).amount as number;
            if (issueId in sumsByIssueId) {
              sumsByIssueId[issueId] += sum;
            } else {
              sumsByIssueId[issueId] = sum;
            }
          }
        }

        const newClientTableItems = await transformDataToTableItem(
          countsByIssueId,
          sumsByIssueId
        );
        setClientTableValue(newClientTableItems);

        const countsByClientMainUid: CountByIssueId = {};
        const sumsByClientMainUid: IssueSum = {};

        for (let i = 0; i < negotiationsData.length; i++) {
          const data = negotiationsData[i];
          const clientMainUid = data.clientMainUid as string;
          const status = data.status as number;
          if (!clientMainUid) continue;

          // count status by clientMainUid
          if (clientMainUid in countsByClientMainUid) {
            if (status in countsByClientMainUid[clientMainUid]) {
              countsByClientMainUid[clientMainUid][status] += 1;
            } else {
              countsByClientMainUid[clientMainUid][status] = 1;
            }
          } else {
            countsByClientMainUid[clientMainUid] = { [status]: 1 };
          }

          // sum orders
          if (
            status >= negotiationStatus.orderApproval &&
            status <= negotiationStatus.posted
          ) {
            const sum = (data.order as DocumentData).amount as number;
            if (clientMainUid in sumsByClientMainUid) {
              sumsByClientMainUid[clientMainUid] += sum;
            } else {
              sumsByClientMainUid[clientMainUid] = sum;
            }
          }
        }

        const newClientTableItemsByRepresentative =
          await transformDataToTableItemByRepresentative(
            countsByClientMainUid,
            sumsByClientMainUid
          );

        setClientStaffTableValue(newClientTableItemsByRepresentative);
      } else if (companyId && props.screenType === screenType.publisher) {
        // メディアの件数で表示だしわけ
        const q = query(mediasRef, where('companyId', '==', companyId));
        const { docs } = await getDocs(q);
        if (docs.length > 0) {
          setSuggestion(t('home.searchIssueSuggest'));
          setSuggestionText(t('home.searchIssueDesc'));
          setSuggestionDetail(t('home.searchIssueDetail'));
          setDetailUrl('/main/publisher/mypage/issue');
          setSuggestionButton(t('home.searchIssue'));
          setButtonLink('/publisher/mypage/issue');
        } else {
          setSuggestion(t('home.mediaSuggest'));
          setSuggestionText(t('home.mediaSuggestDesc'));
          setSuggestionDetail(t('home.mediaDetail'));
          setDetailUrl('');
          setSuggestionButton(t('home.addMedia'));
          setButtonLink('/publisher/mypage/media/create');
        }
        setTableItem([
          t('home.tableItem.mediaName'),
          t('home.tableItem.like'),
          t('home.tableItem.proposalPreparation'),
          t('home.tableItem.negotiation'),
          t('home.tableItem.orderApproval'),
          t('home.tableItem.amount')
        ]);
        const user = await userSnapshot(currentUser?.uid ?? '');
        const negotiationsRef = collection(db, 'negotiations');
        const negotiations = query(
          negotiationsRef,
          where('publisherId', '==', user.data()?.companyId)
        );

        const negotiationsSnapshot = await getDocs(negotiations);

        let negotiationsData = negotiationsSnapshot.docs.map((document) =>
          document.data()
        );

        await Promise.all(
          negotiationsData.map(async (negotiation, index) => {
            const ordersSnapshot = await getDocs(
              collection(
                doc(negotiationsRef, negotiation.negotiationId as string),
                'orders'
              )
            );
            const ordersData = ordersSnapshot.docs.map((document) =>
              document.data()
            );

            // Assuming there's always one order in the subcollection
            negotiationsData[index].order = ordersData[0];
          })
        );

        negotiationsData = negotiationsData.filter((negotiation) => {
          const approvedDate = (
            (negotiation.order as DocumentData)?.approvedDate as Timestamp
          )?.toDate();
          // both start and end date provided
          if (
            startDate.start &&
            startDate.end &&
            approvedDate >= startDate.start.toDate() &&
            approvedDate <= startDate.end.toDate()
          ) {
            return true;
          }
          // only start date provided
          if (
            startDate.start &&
            !startDate.end &&
            approvedDate >= startDate.start.toDate()
          ) {
            return true;
          }
          // only end date provided
          if (
            !startDate.start &&
            startDate.end &&
            approvedDate <= startDate.end.toDate()
          ) {
            return true;
          }
          // neither start nor end date provided
          if (!startDate.start && !startDate.end) {
            return true;
          }

          return false;
        });

        const countsByMediaId: CountByIssueId = {};
        const sumsByMediaId: IssueSum = {};

        // Process each negotiations doc and corresponding orders docs
        for (let i = 0; i < negotiationsData.length; i++) {
          const data = negotiationsData[i];
          const mediaId = data.mediaId as string;
          const status = data.status as number;

          // count status by issueId
          if (mediaId in countsByMediaId) {
            if (status in countsByMediaId[mediaId]) {
              countsByMediaId[mediaId][status] += 1;
            } else {
              countsByMediaId[mediaId][status] = 1;
            }
          } else {
            countsByMediaId[mediaId] = { [status]: 1 };
          }

          // sum orders
          if (
            status >= negotiationStatus.orderApproval &&
            status <= negotiationStatus.posted
          ) {
            const sum = (data.order as DocumentData).amount as number;
            if (mediaId in sumsByMediaId) {
              sumsByMediaId[mediaId] += sum;
            } else {
              sumsByMediaId[mediaId] = sum;
            }
          }
        }

        const newPublisherTableItems = await transformDataToTableItemPublisher(
          countsByMediaId,
          sumsByMediaId
        );
        setPublisherTableValue(newPublisherTableItems);

        const countsByPublisherMainUid: CountByPublisherMainUidAndMediaId = {};
        const sumsByPublisherMainUid: SumByPublisherMainUidAndMediaId = {};
        // 同じことをpublisherMainUidとmediaIdで行う
        for (let i = 0; i < negotiationsData.length; i++) {
          const data = negotiationsData[i];
          const publisherMainUid = data.publisherMainUid as string;
          const status = data.status as number;
          const mediaId = data.mediaId as string;
          if (!publisherMainUid || !mediaId) continue;

          // count status by clientMainUid and mediaId
          if (publisherMainUid in countsByPublisherMainUid) {
            if (mediaId in countsByPublisherMainUid[publisherMainUid]) {
              if (
                status in countsByPublisherMainUid[publisherMainUid][mediaId]
              ) {
                countsByPublisherMainUid[publisherMainUid][mediaId][
                  status
                ] += 1;
              } else {
                countsByPublisherMainUid[publisherMainUid][mediaId][status] = 1;
              }
            } else {
              countsByPublisherMainUid[publisherMainUid][mediaId] = {
                [status]: 1
              };
            }
          } else {
            countsByPublisherMainUid[publisherMainUid] = {
              [mediaId]: { [status]: 1 }
            };
          }

          // sum orders
          if (
            status >= negotiationStatus.orderApproval &&
            status <= negotiationStatus.posted
          ) {
            const sum = (data.order as DocumentData).amount as number;
            if (publisherMainUid in sumsByPublisherMainUid) {
              if (mediaId in sumsByPublisherMainUid[publisherMainUid]) {
                sumsByPublisherMainUid[publisherMainUid][mediaId] += sum;
              } else {
                sumsByPublisherMainUid[publisherMainUid][mediaId] = sum;
              }
            } else {
              sumsByPublisherMainUid[publisherMainUid] = { [mediaId]: sum };
            }
          }
        }

        console.log(countsByPublisherMainUid, 'countsByPublisherMainUid');
        console.log(sumsByPublisherMainUid, 'sumsByPublisherMainUid');

        const newPublisherTableItemsByRepresentative =
          await transformDataToTableItemByRepresentativePublisher(
            countsByPublisherMainUid,
            sumsByPublisherMainUid
          );

        setPublisherStaffTableValue(newPublisherTableItemsByRepresentative);
      } else if (companyId && props.screenType === screenType.agent) {
        const user = await userSnapshot(currentUser?.uid ?? '');
        const q = query(issuesRef, where('companyId', '==', companyId));
        const { docs } = await getDocs(q);
        if (docs.length > 0) {
          setSuggestion(t('home.mediaInquiry'));
          setSuggestionText(t('home.mediaInquiryDesc'));
          setSuggestionDetail(t('home.searchMediaDetail'));
          setDetailUrl('');
          setSuggestionButton(t('home.searchMedia'));
          setButtonLink('/agent/mypage/media');
        } else {
          setSuggestion(t('home.issueSuggest'));
          setSuggestionText(t('home.issueSuggestDesc'));
          setSuggestionDetail(t('home.issueDetail'));
          setDetailUrl('');
          setSuggestionButton(t('home.addIssue'));
          setButtonLink('/agent/mypage/issue/edit?type=add');
        }
        setTableItem([
          t('home.tableItem.requestProposal'),
          t('home.tableItem.negotiation'),
          t('home.tableItem.preparingPub'),
          t('home.tableItem.alreadyPub'),
          t('home.tableItem.amount')
        ]);
        const negotiationsRef = collection(db, 'negotiations');
        const negotiationsQuery = query(
          negotiationsRef,
          where('agentId', '==', user.data()?.companyId)
        );

        const negotiationsSnapshot = await getDocs(negotiationsQuery);

        let negotiationsData = negotiationsSnapshot.docs.map((document) =>
          document.data()
        );

        await Promise.all(
          negotiationsData.map(async (negotiation, index) => {
            const ordersSnapshot = await getDocs(
              collection(
                doc(negotiationsRef, negotiation.negotiationId as string),
                'orders'
              )
            );
            const ordersData = ordersSnapshot.docs.map((document) =>
              document.data()
            );

            // Assuming there's always one order in the subcollection
            negotiationsData[index].order = ordersData[0];
          })
        );

        negotiationsData = negotiationsData.filter((negotiation) => {
          const approvedDate = (
            (negotiation.order as DocumentData)?.approvedDate as Timestamp
          )?.toDate();
          // both start and end date provided
          if (
            startDate.start &&
            startDate.end &&
            approvedDate >= startDate.start.toDate() &&
            approvedDate <= startDate.end.toDate()
          ) {
            return true;
          }
          // only start date provided
          if (
            startDate.start &&
            !startDate.end &&
            approvedDate >= startDate.start.toDate()
          ) {
            return true;
          }
          // only end date provided
          if (
            !startDate.start &&
            startDate.end &&
            approvedDate <= startDate.end.toDate()
          ) {
            return true;
          }
          // neither start nor end date provided
          if (!startDate.start && !startDate.end) {
            return true;
          }

          return false;
        });
        const statusCounts: Record<string, number> = {};

        negotiationsData.forEach((data) => {
          const status = data.status as number;
          statusCounts[status] = (statusCounts[status] || 0) + 1;
        });

        let totalAmount = 0;

        negotiationsData
          .filter((negotiationData) => {
            const status = negotiationData.status as number;
            return (
              status >= negotiationStatus.orderApproval &&
              status <= negotiationStatus.posted
            );
          })
          .forEach((negotiationData) => {
            totalAmount += (negotiationData.order as DocumentData)
              .amount as number;
          });

        const companySnap = await companySnapshot(
          user.data()?.companyId as string
        );

        const icon = await getIcon(companySnap);

        if (totalAmount === 0 && negotiationsData.length === 0) {
          setAgentTableValue([]);
        } else {
          setAgentTableValue([
            {
              amount: totalAmount,
              icon,
              name: companySnap.data()?.name as string,
              negotiation: statusCounts[negotiationStatus.negotiation] || 0,
              published: statusCounts[negotiationStatus.posted] || 0,
              requestProposal:
                statusCounts[negotiationStatus.proposalPreparation] || 0,
              waitPublished: statusCounts[negotiationStatus.preparation] || 0
            }
          ]);
        }

        const countsByAgentMainUid: CountByIssueId = {};
        const sumsByAgentMainUid: IssueSum = {};

        for (let i = 0; i < negotiationsData.length; i++) {
          const data = negotiationsData[i];
          const agentMainUid = data.agentMainUid as string;
          const status = data.status as number;
          if (!agentMainUid) continue;

          // count status by clientMainUid
          if (agentMainUid in countsByAgentMainUid) {
            if (status in countsByAgentMainUid[agentMainUid]) {
              countsByAgentMainUid[agentMainUid][status] += 1;
            } else {
              countsByAgentMainUid[agentMainUid][status] = 1;
            }
          } else {
            countsByAgentMainUid[agentMainUid] = { [status]: 1 };
          }

          // sum orders
          if (
            status >= negotiationStatus.orderApproval &&
            status <= negotiationStatus.posted
          ) {
            const sum = (data.order as DocumentData).amount as number;
            if (agentMainUid in sumsByAgentMainUid) {
              sumsByAgentMainUid[agentMainUid] += sum;
            } else {
              sumsByAgentMainUid[agentMainUid] = sum;
            }
          }
        }

        const newAgentTableItemsByRepresentative =
          await transformDataToTableItemByRepresentativeAgent(
            countsByAgentMainUid,
            sumsByAgentMainUid
          );

        setAgentStaffTableValue(newAgentTableItemsByRepresentative);
      }
    })();
  }, [props.screenType, companyId, startDate]);

  const clientUsefull = (): void => {
    const clientGoogleDriveLink =
      'https://drive.google.com/drive/folders/1ROCNqhkuHblnFZ7dxv4Mk8aIeD2VoFYW?usp=share_link';
    window.open(clientGoogleDriveLink, '_blank');
  };
  const publisherUsefull = (): void => {
    const publisherGoogleDriveLink =
      'https://drive.google.com/drive/folders/1Sy41Q7ba32oyrBhExbOZb1Kt5LNPLUZR?usp=share_link';
    window.open(publisherGoogleDriveLink, '_blank');
  };

  const agentUsefull = (): void => {
    const agentGoogleDriveLink =
      'https://drive.google.com/drive/folders/1j5wohBhLk8diq34GrAKwTLOZuhuOPMky?usp=share_link';
    window.open(agentGoogleDriveLink, '_blank');
  };

  return (
    <Typography
      component="div"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        position: 'static',
        width: '100%'
      }}
    >
      <Typography sx={styles.mypageHeader} component="div">
        <Typography sx={styles.mypageHeaderTitle}>
          {t('mypage.home')}
        </Typography>
        <Button
          variant="outlined"
          sx={[styles.tertiaryButtonBackground, styles.contactButton]}
          onClick={() => {
            navigate(contactUrl, {
              state: {
                companyName: company.name,
                contactKind: contactKind.fromLogin,
                email: company.email,
                type: screenTypeHelper()
              }
            });
          }}
        >
          <Typography component="span" sx={styles.contactButtonText}>
            {contact}
          </Typography>
        </Button>
      </Typography>
      <Card sx={styles.card}>
        <Typography sx={styles.mypageHeaderTitle}>
          {t('home.activity')}
        </Typography>
        <Card sx={styles.todoCard}>
          <Typography sx={styles.todo}>{t('home.todo')}</Typography>
          <Grid sx={styles.todoBody}>
            <Typography sx={styles.todoText}>{suggestion}</Typography>
            <Typography style={{ marginTop: '16px' }}>
              {suggestionText}
            </Typography>
            <Typography style={{ marginTop: '16px' }}>
              <Link href={detailUrl} sx={styles.todoLink}>
                {suggestionDetail}
              </Link>
            </Typography>

            <Typography style={{ marginTop: '24px' }}>
              <Button
                color="error"
                variant="contained"
                sx={primaryButtonBackground}
                onClick={() => navigate(buttonLink)}
              >
                {suggestionButton}
              </Button>
            </Typography>
          </Grid>
        </Card>
        {(Boolean(startDate.start) ||
          Boolean(startDate.end) ||
          (props.screenType === screenType.client &&
            clientTableValue.length > 0) ||
          (props.screenType === screenType.publisher &&
            publisherTableValue.length > 0) ||
          (props.screenType === screenType.agent &&
            agentTableValue.length > 0)) && (
          <Grid
            container
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            rowGap="6px"
            sx={{
              '@media screen and (max-width: 800px)': {
                alignItems: 'flex-start',
                flexDirection: 'column-reverse'
              },
              flexWrap: 'unset',
              margin: '24px 0 8px',
              width: 'none'
            }}
          >
            <Grid
              item
              sx={{
                '@media screen and (max-width: 800px)': {
                  width: '100%'
                }
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    color="error"
                    checked={showStaffData}
                    onChange={handleSwitchChange}
                    sx={{ margin: 0 }}
                  />
                }
                sx={{ width: '250px' }}
                label={t('home.showStaffData')}
              />
            </Grid>

            <Grid
              item
              container
              flexDirection="row"
              justifyContent="flex-end"
              alignItems="center"
              columnGap="12px"
              sx={{
                '@media screen and (max-width: 600px)': {
                  alignItems: 'flex-start',
                  flexDirection: 'column'
                },
                '@media screen and (max-width: 800px)': {
                  justifyContent: 'flex-start',
                  maxWidth: '100%'
                }
              }}
            >
              <Grid item>
                <Typography>{t('home.searchDate')}</Typography>
              </Grid>
              <Grid
                item
                container
                flexDirection="row"
                alignItems="center"
                columnGap="6px"
                width="unset"
                sx={{
                  '@media screen and (max-width: 360px)': {
                    justifyContent: 'flex-start'
                  },
                  justifyContent: 'flex-end'
                }}
              >
                <Grid item>
                  <CustomDatePicker
                    isRequired={false}
                    placeholder="2022/08/24"
                    formMb="zero"
                    width={'search'}
                    value={startDate.start}
                    setValue={(e) => {
                      setStartDate({ ...startDate, start: e });
                    }}
                  />
                </Grid>
                <Grid item>
                  <Typography sx={{ textAlign: 'center' }} component="p">
                    〜
                  </Typography>
                </Grid>
                <Grid item>
                  <CustomDatePicker
                    isRequired={false}
                    formMb="zero"
                    width={'search'}
                    error={false}
                    placeholder="2022/08/24"
                    value={startDate.end}
                    setValue={(e) => setStartDate({ ...startDate, end: e })}
                  />
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    sx={[tertiaryButtonBackground, { color: '#757575' }]}
                    onClick={() => {
                      setStartDate({ end: null, start: null });
                    }}
                  >
                    {t('home.clear')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}

        <TableContainer component={Paper} sx={styles.table}>
          <Table sx={styles.tableWrapper}>
            {(Boolean(startDate.start) ||
              Boolean(startDate.end) ||
              (props.screenType === screenType.client &&
                clientTableValue.length > 0) ||
              (props.screenType === screenType.publisher &&
                publisherTableValue.length > 0) ||
              (props.screenType === screenType.agent &&
                agentTableValue.length > 0)) && (
              <TableHead style={{ backgroundColor: '#F2F2F2' }}>
                <TableRow sx={styles.tableRow}>
                  <TableCell sx={styles.tableCell}></TableCell>
                  {tableItem.map((item, index) => (
                    <TableCell
                      key={index}
                      sx={[styles.tableCell, styles.tableHeaderCell]}
                    >
                      {item}
                      {item === t('home.tableItem.requestProposal') ? (
                        <CustomGuideButton
                          title={item}
                          guideText={t('guideText.home.requestProposal')}
                        />
                      ) : item === t('home.tableItem.negotiation') &&
                        props.screenType !== screenType.publisher ? (
                        <CustomGuideButton
                          title={item}
                          guideText={t('guideText.home.negotiation')}
                        />
                      ) : item === t('home.tableItem.negotiation') &&
                        props.screenType === screenType.publisher ? (
                        <CustomGuideButton
                          title={item}
                          guideText={t('guideText.home.negotiationPub')}
                        />
                      ) : item === t('home.tableItem.preparingPub') ? (
                        <CustomGuideButton
                          title={item}
                          guideText={t('guideText.home.preparingPub')}
                        />
                      ) : item === t('home.tableItem.alreadyPub') ? (
                        <CustomGuideButton
                          title={item}
                          guideText={t('guideText.home.alreadyPub')}
                        />
                      ) : item === t('home.tableItem.like') ? (
                        <CustomGuideButton
                          title={item}
                          guideText={t('guideText.home.like')}
                        />
                      ) : item === t('home.tableItem.proposalPreparation') ? (
                        <CustomGuideButton
                          title={item}
                          guideText={t('guideText.home.proposalPreparation')}
                        />
                      ) : item === t('home.tableItem.orderApproval') ? (
                        <CustomGuideButton
                          title={item}
                          guideText={t('guideText.home.orderApproval')}
                        />
                      ) : (
                        ''
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            )}

            {props.screenType === screenType.client && (
              <TableBody>
                {showData &&
                  clientTableValue.map((item, index) => (
                    <TableRow sx={styles.tableRow} key={index}>
                      <TableCell sx={[styles.tableCell, styles.tableNameCell]}>
                        <Avatar alt="logo" src={item.icon} sx={styles.logo} />
                        <Typography sx={styles.tableName}>
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.requestProposal}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.negotiation}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.waitPublished}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.published}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[
                          styles.tableCell,
                          styles.tableNumberCell,
                          styles.tableAmountCell
                        ]}
                      >
                        {item.amount
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.amountUnit')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                {showStaffData &&
                  clientStaffTableValue.map((item, index) => (
                    <TableRow sx={styles.tableRow} key={index}>
                      <TableCell sx={[styles.tableCell, styles.tableNameCell]}>
                        <Avatar alt="logo" src={item.icon} sx={styles.logo} />
                        <Typography sx={styles.tableName}>
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.requestProposal}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.negotiation}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.waitPublished}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.published}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[
                          styles.tableCell,
                          styles.tableNumberCell,
                          styles.tableAmountCell
                        ]}
                      >
                        {item.amount
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.amountUnit')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            )}

            {props.screenType === screenType.publisher && (
              <TableBody>
                {showData &&
                  publisherTableValue.map((item, index) => (
                    <TableRow sx={styles.tableRow} key={index}>
                      <TableCell sx={[styles.tableCell, styles.tableNameCell]}>
                        <Avatar alt="logo" src={item.icon} sx={styles.logo} />
                        <Typography sx={styles.tableName}>
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableMediaNameCell]}
                      >
                        {item.mediaName}
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.like}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.proposalPreparation}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.negotiation}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.orderApproval}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[
                          styles.tableCell,
                          styles.tableNumberCell,
                          styles.tableAmountCell
                        ]}
                      >
                        {item.amount
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.amountUnit')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                {showStaffData &&
                  publisherStaffTableValue.map((item, index) => (
                    <TableRow sx={styles.tableRow} key={index}>
                      <TableCell sx={[styles.tableCell, styles.tableNameCell]}>
                        <Avatar alt="logo" src={item.icon} sx={styles.logo} />
                        <Typography sx={styles.tableName}>
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableMediaNameCell]}
                      >
                        {item.mediaName}
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.like}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.proposalPreparation}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.negotiation}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.orderApproval}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[
                          styles.tableCell,
                          styles.tableNumberCell,
                          styles.tableAmountCell
                        ]}
                      >
                        {item.amount
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.amountUnit')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            )}

            {props.screenType === screenType.agent && (
              <TableBody>
                {showData &&
                  agentTableValue.map((item, index) => (
                    <TableRow sx={styles.tableRow} key={index}>
                      <TableCell sx={[styles.tableCell, styles.tableNameCell]}>
                        <Avatar alt="logo" src={item.icon} sx={styles.logo} />
                        <Typography sx={styles.tableName}>
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.requestProposal}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.negotiation}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.waitPublished}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.published}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[
                          styles.tableCell,
                          styles.tableNumberCell,
                          styles.tableAmountCell
                        ]}
                      >
                        {item.amount
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.amountUnit')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                {showStaffData &&
                  agentStaffTableValue.map((item, index) => (
                    <TableRow sx={styles.tableRow} key={index}>
                      <TableCell sx={[styles.tableCell, styles.tableNameCell]}>
                        <Avatar alt="logo" src={item.icon} sx={styles.logo} />
                        <Typography sx={styles.tableName}>
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.requestProposal}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.negotiation}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.waitPublished}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[styles.tableCell, styles.tableNumberCell]}
                      >
                        {item.published}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.numberUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={[
                          styles.tableCell,
                          styles.tableNumberCell,
                          styles.tableAmountCell
                        ]}
                      >
                        {item.amount
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        <Typography component="span" sx={styles.tableUnit}>
                          {t('home.tableItem.amountUnit')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Card>
      {props.screenType === screenType.client && (
        <Button
          onClick={clientUsefull}
          sx={[blueButton, styles.usefulButton]}
          variant="contained"
          color="primary"
        >
          {t('home.usefulGuid')}
          <LaunchIcon sx={{ marginLeft: '6px' }} />
        </Button>
      )}
      {props.screenType === screenType.publisher && (
        <Button
          onClick={publisherUsefull}
          sx={[blueButton, styles.usefulButton]}
          variant="contained"
          color="primary"
        >
          {t('home.usefulGuid')}
          <LaunchIcon sx={{ marginLeft: '6px' }} />
        </Button>
      )}
      {props.screenType === screenType.agent && (
        <Button
          onClick={agentUsefull}
          sx={[blueButton, styles.usefulButton]}
          variant="contained"
          color="primary"
        >
          {t('home.usefulGuid')}
          <LaunchIcon sx={{ marginLeft: '6px' }} />
        </Button>
      )}
    </Typography>
  );
};

export default MypageHome;
